import { Row } from '@tanstack/react-table';
import clsx from 'clsx';
import React, {
  MutableRefObject,
  ReactNode,
  useCallback,
  useMemo,
} from 'react';
import {
  GroupedVirtuoso,
  GroupedVirtuosoHandle,
  ListItem,
} from 'react-virtuoso';
import { PageSection } from 'src/components/common/PageSection';
import { useColumnResizingContext } from 'src/contexts/ColumnResizingContext/ColumnResizingContext';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { TableTd } from 'src/tables/Table/components/TableTd';
import { PosGroupedVirtuosoContext } from 'src/tables/Table/GroupedVirtuosoTable/PosGroupedVirtuosoContext';
import { PosGroupedVirtuosoGroup } from 'src/tables/Table/GroupedVirtuosoTable/PosGroupedVirtuosoGroup';
import { PosGroupedVirtuosoItem } from 'src/tables/Table/GroupedVirtuosoTable/PosGroupedVirtuosoItem';
import { PosGroupedVirtuosoList } from 'src/tables/Table/GroupedVirtuosoTable/PosGroupedVirtuosoList';
import { PosGroupedVirtuosoScroller } from 'src/tables/Table/GroupedVirtuosoTable/PosGroupedVirtuosoScroller';
import { groupedVirtuosoWrapper } from 'src/tables/Table/GroupedVirtuosoTable/PosGroupedVirtuosoTable.css';
import { useGetVirtuosoGroups } from 'src/tables/Table/GroupedVirtuosoTable/useGetVirtuosoGroups';
import {
  cellNoWrapStyle,
  groupRow,
  groupRowFirstCell,
  groupValue,
} from 'src/tables/Table/GroupRow.css';

import { PosTableData } from '../Table';
import { PosGroupedVirtuosoFooter } from './PosGroupedVirtuosoFooter';

interface GroupedVirtuosoTableProps<T extends PosTableData> {
  context: PosGroupedVirtuosoContext<T>;
  renderRowContent: (row: Row<T>) => ReactNode;
  scrollRef?: MutableRefObject<HTMLElement | null>;
  virtuosoRef?: React.ForwardedRef<GroupedVirtuosoHandle>;
  itemsRendered?: (rows: Row<T>[]) => void;
  endReached?: (index: number) => void;
}

const groupedVirtuosoComponents = {
  List: PosGroupedVirtuosoList,
  Group: PosGroupedVirtuosoGroup,
  Item: PosGroupedVirtuosoItem,
  Scroller: PosGroupedVirtuosoScroller,
  Footer: PosGroupedVirtuosoFooter,
};

const OverscanLength = window.innerHeight / 2;
const DefaultItemHeight = 36;

export const PosGroupedVirtuosoTable = <T extends PosTableData>({
  context,
  renderRowContent,
  scrollRef,
  virtuosoRef,
  itemsRendered,
  endReached,
}: GroupedVirtuosoTableProps<T>) => {
  const { table } = context;
  const isGrouping = table.getState().grouping.length > 0;
  const isLargeDesktop = useMatchMedia('largeDesktop');
  const { isResizeEnabled } = useColumnResizingContext();

  const { groupCounts, rowHeaderGroups, allSingleRows } = useGetVirtuosoGroups({
    table,
  });

  const groupedContext = useMemo<PosGroupedVirtuosoContext<T>>(
    () => ({
      ...context,
      tableRows: allSingleRows,
    }),
    [allSingleRows, context]
  );

  const groupContentRenderer = useCallback(
    (groupIndex: number) => {
      const rowsGroup: Row<T> = rowHeaderGroups[groupIndex];

      // Used when there is no grouping applied
      if (!rowHeaderGroups.length) {
        return <td />;
      }

      return (
        <>
          {rowsGroup.getVisibleCells().map((cell, index) => {
            const isFirstCell = index === 0;
            return (
              <TableTd
                data-group={true} // Keep this to not apply padding
                key={cell.id}
                isLargeDesktop={isLargeDesktop}
                isGrouping={isGrouping}
                isResizeEnabled={isResizeEnabled}
                cell={cell}
                classNames={groupRow}
                tableCellStyle={isFirstCell ? cellNoWrapStyle : undefined}
              >
                <PageSection.Header
                  headerClassName={clsx({
                    [groupRowFirstCell]: isFirstCell,
                  })}
                >
                  {isFirstCell ? (
                    <span className={groupValue}>
                      {rowsGroup.groupingValue as string}
                    </span>
                  ) : null}
                </PageSection.Header>
              </TableTd>
            );
          })}
        </>
      );
    },
    [isGrouping, isLargeDesktop, isResizeEnabled, rowHeaderGroups]
  );

  const itemContentRenderer = useCallback(
    (globalIndex: number) => {
      return renderRowContent(allSingleRows[globalIndex]);
    },
    [allSingleRows, renderRowContent]
  );

  const setScrollerRef = useCallback(
    (el: unknown) => {
      if (el instanceof HTMLElement && scrollRef) {
        scrollRef.current = el;
      }
    },
    [scrollRef]
  );

  const onItemsRendered = useCallback(
    (items: ListItem<any>[]) => {
      if (!itemsRendered) return;
      const renderedRows = items
        .map(({ index }) =>
          allSingleRows.length > index ? allSingleRows[index] : null
        )
        .filter((row: Row<T> | null): row is Row<T> => row != null);
      itemsRendered(renderedRows);
    },
    [allSingleRows, itemsRendered]
  );

  return (
    <GroupedVirtuoso
      ref={virtuosoRef}
      className={groupedVirtuosoWrapper}
      components={groupedVirtuosoComponents}
      groupCounts={groupCounts}
      context={groupedContext}
      scrollerRef={setScrollerRef}
      groupContent={groupContentRenderer}
      itemContent={itemContentRenderer}
      itemsRendered={onItemsRendered}
      endReached={endReached}
      overscan={{ main: OverscanLength, reverse: OverscanLength }}
      increaseViewportBy={{ top: OverscanLength, bottom: OverscanLength }}
      defaultItemHeight={DefaultItemHeight}
    />
  );
};
